import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import EmailListInput from '../../components/EmailListInput';
import Button from '../../components/Button';
import SkeletonLoader from '../../components/SkeletonLoader';
import notificationSettingApi from '../../api/notificationSettingApi';
import { useToast } from '../../contexts/ToastContext';
import { ROUTES } from '../../constants';

const NotificationSettings = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [validationErrors, setValidationErrors] = useState({});
  const [generalError, setGeneralError] = useState(null);
  const [formData, setFormData] = useState({
    to: [],
    cc: [],
    bcc: [],
  });
  const [recordExists, setRecordExists] = useState(false);

  useEffect(() => {
    const fetchNotificationSetting = async () => {
      setLoading(true);
      setGeneralError(null);
      try {
        const response = await notificationSettingApi.getNotificationSetting();
        if (response.success && response.data) {
          setFormData({
            to: response.data.to || [],
            cc: response.data.cc || [],
            bcc: response.data.bcc || [],
          });
          setRecordExists(true);
        } else {
          setFormData({ to: [], cc: [], bcc: [] });
          setRecordExists(false);
        }
      } catch (err) {
        console.error('Failed to fetch Notification Setting:', err);
        const errorMessage = err.response?.data?.message || 'Failed to load Notification Setting. Please try again.';
        addToast(errorMessage, 'error');
        setGeneralError(errorMessage);
        setRecordExists(false);
      } finally {
        setLoading(false);
      }
    };

    fetchNotificationSetting();
  }, [addToast]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setGeneralError(null);
    setValidationErrors({});

    const dataToSend = formData;

    try {
      if (recordExists) {
        await notificationSettingApi.updateNotificationSetting(dataToSend);
        addToast('Notification Setting updated successfully!', 'success');
      } else {
        await notificationSettingApi.createNotificationSetting(dataToSend);
        addToast('Notification Setting created successfully!', 'success');
        setRecordExists(true);
      }
    } catch (err) {
      console.error('Failed to save Notification Setting:', err);
      if (err.response && err.response.status === 422) {
        setValidationErrors(err.response.data.errors || {});
        addToast(err.response.data.message || 'Validation failed. Please check your input.', 'error');
      } else {
        const errorMessage = err.response?.data?.message || 'Failed to save Notification Setting. Please try again.';
        addToast(errorMessage, 'error');
        setGeneralError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white text-center">
        <SkeletonLoader type="form" />
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white">
      <h1 className="text-3xl font-bold mb-6">{recordExists ? 'Edit Notification Settings' : 'Create Notification Settings'}</h1>
      {generalError && <div className="text-red-500 mb-4">{generalError}</div>}
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <EmailListInput
            label="To Emails"
            name="to"
            value={formData.to}
            onChange={handleChange}
            fieldErrors={validationErrors}
          />
          <EmailListInput
            label="CC Emails"
            name="cc"
            value={formData.cc}
            onChange={handleChange}
            fieldErrors={validationErrors}
          />
          <EmailListInput
            label="BCC Emails"
            name="bcc"
            value={formData.bcc}
            onChange={handleChange}
            fieldErrors={validationErrors}
          />
        </div>
        <div className="flex items-center justify-between mt-6">
          <Button
            type="submit"
            className="bg-accent-red hover:bg-red-700 text-white"
            loading={loading}
          >
            {recordExists ? 'Update Settings' : 'Create Settings'}
          </Button>
          <Button
            type="button"
            onClick={() => navigate(ROUTES.DASHBOARD)}
            className="bg-gray-500 hover:bg-gray-600 text-white"
            disabled={loading}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default NotificationSettings;